/**
 * Centralized axios configuration and error handling.
 * Adds response interceptor to show user-friendly error messages.
 */
import axios from "axios";
import { toast } from "react-toastify";
import i18n from "../lang/i18n";
import { parseApiError, getDisplayMessage } from "./errorMessages";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Set default baseURL for axios
axios.defaults.baseURL = baseURL;
axios.defaults.timeout = 30000; // 30 seconds
axios.defaults.headers.common["Content-Type"] = "application/json";

/**
 * Response interceptor: transform errors into user-friendly toasts.
 * Use config.skipGlobalErrorHandler = true to opt out (e.g. for silent fetches).
 */
const setupResponseInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error?.config;

      // Skip global error handling if requested (e.g. for 404 on optional resources)
      if (config?.skipGlobalErrorHandler === true) {
        return Promise.reject(error);
      }

      const parsed = parseApiError(error);
      const message = getDisplayMessage(parsed, (key, opts) =>
        i18n.t(key, { ...opts, ns: "common" })
      );

      // Show toast - use error variant for visibility
      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });

      return Promise.reject(error);
    }
  );
};

setupResponseInterceptor();

export default axios;
